import type { ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { JobNavigation } from '@/features/call-center/components/JobNavigation'
import styles from './JobCheckout.module.css'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Icon } from '@/components/Icon'
import { formatMoney } from '@/utils/money-util'
import { FormGroup, FormRow } from '@/components/Form'
import { TextInput } from '@/components/inputs/TextInput'
import { ContactCard } from '@/features/call-center/ClientDetails'

export interface JobCheckoutLinkProps {
  checkoutLink: string
  isEmailingCheckoutLink: boolean
  onSendEmail: () => Promise<void>
  onCopy: () => void
}

export interface JobCheckoutProps {
  contact: {
    fullName: string
    email: string
    phone: string
    workspace: string
  }
  amountDueNow: number
  priceBreakdown: ReactNode
  showAddCoupon: boolean
  onAddCoupon: () => unknown
  /**
   * Shown when the job can't be submitted directly and has to be paid via link.
   */
  checkoutLink: JobCheckoutLinkProps | null
  onPrevious: () => unknown
  isSubmitPending: boolean
  canSubmit: boolean
  onSubmit: () => void
  couponDialog: ReactNode
}

export function JobCheckout({
  contact,
  amountDueNow,
  priceBreakdown,
  showAddCoupon,
  onAddCoupon,
  checkoutLink,
  onPrevious,
  isSubmitPending,
  canSubmit,
  onSubmit,
  couponDialog,
}: JobCheckoutProps) {
  return (
    <>
      <ContactCard
        fullName={contact.fullName}
        email={contact.email}
        phone={contact.phone}
        workspace={contact.workspace}
      />
      <br />
      <div className={styles.root}>
        <Card unconstrained>
          <CardHeader>
            <CardTitle>$ Pricing</CardTitle>
          </CardHeader>

          <CardContent scroll>
            <div className={styles.content}>
              <div className={styles.pricing}>
                <div className={styles.total}>{formatMoney(amountDueNow)}</div>
                <div>Amount due now</div>
              </div>

              {priceBreakdown}

              <div className={styles.checkoutButtons}>
                {showAddCoupon && (
                  <Button
                    label="Add Coupon"
                    onPress={() => onAddCoupon()}
                    size="cta"
                    variant="primary"
                  />
                )}
              </div>

              {checkoutLink && <CheckoutLink {...checkoutLink} />}
            </div>
          </CardContent>
        </Card>

        <JobNavigation
          secondaryAction={
            <PreviousButton onPress={onPrevious} isDisabled={isSubmitPending} />
          }
          primaryAction={
            canSubmit ? (
              <SubmitJobButton isPending={isSubmitPending} onPress={onSubmit} />
            ) : null
          }
        />

        {couponDialog}
      </div>
    </>
  )
}

interface SubmitJobButtonProps {
  isPending: boolean
  onPress: () => void
}

function SubmitJobButton({ isPending, onPress }: SubmitJobButtonProps) {
  return (
    <Button
      label="Submit"
      onPress={onPress}
      isPending={isPending}
      isDisabled={isPending}
      variant="primary"
    />
  )
}

interface PreviousButtonProps {
  isDisabled: boolean
  onPress: () => void
}

function PreviousButton({ isDisabled, onPress }: PreviousButtonProps) {
  return (
    <Button
      label="Previous"
      isDisabled={isDisabled}
      onPress={onPress}
      variant="secondary"
    />
  )
}

export interface PriceBreakdownProps {
  scopeItemNames: string[]
  hasCoupons: boolean
  discountLabel: ReactNode
  couponDetail?: ReactNode
  primaryAmount: ReactNode
  discount: ReactNode
  total: ReactNode
}

export function PriceBreakdown({
  scopeItemNames,
  hasCoupons,
  discountLabel,
  couponDetail,
  primaryAmount,
  discount,
  total,
}: PriceBreakdownProps) {
  return (
    <div className={styles.breakdown}>
      <b>Services</b>
      <div className={styles.items}>
        {scopeItemNames.map((item, index) => (
          <div key={`${index}-${item}`} className={styles.item}>
            <div>{item}</div>
          </div>
        ))}
      </div>

      {hasCoupons && (
        <>
          <div className={styles.item}>
            <div>Discount coupon</div>
            <div>{discountLabel}</div>
          </div>

          {couponDetail}
        </>
      )}

      <b>Price Breakdown</b>
      <div className={styles.item}>
        <div>Primary Amount</div>
        <div>{primaryAmount}</div>
      </div>
      <div className={styles.item}>
        <div>Coupon</div>
        <div>{discount}</div>
      </div>
      <div className={styles.item}>
        <div>Total</div>
        <div>{total}</div>
      </div>
    </div>
  )
}

export interface CouponDetailProps {
  name: ReactNode
  description: ReactNode
  discount: ReactNode
  isRemoving: boolean
  onRemove: () => unknown
}

export function CouponDetail({
  name,
  description,
  discount,
  isRemoving,
  onRemove,
}: CouponDetailProps) {
  return (
    <div className={styles.couponSummary}>
      <div className={styles.appliedBadge}>
        <Icon size={12} name="tick" /> Applied
      </div>
      <div>
        <div>{name}</div>
        <div>{description}</div>
      </div>
      <div>{discount}</div>
      <IconButton
        name="close"
        label="Remove coupon"
        color="var(--red500)"
        isPending={isRemoving}
        onPress={onRemove}
        size={24}
      />
    </div>
  )
}

function CheckoutLink({
  checkoutLink,
  isEmailingCheckoutLink,
  onSendEmail,
  onCopy,
}: JobCheckoutLinkProps) {
  const [copied, setCopied] = useState(false)

  const copyUrl = useCallback(async () => {
    await navigator.clipboard.writeText(checkoutLink)
    onCopy()
    setCopied(true)
  }, [checkoutLink, onCopy])

  useEffect(() => {
    let timerId = null

    if (copied) {
      timerId = setTimeout(() => setCopied(false), 2000)
    }

    return () => {
      if (timerId) {
        clearTimeout(timerId)
      }
    }
  }, [copied])

  return (
    <FormRow>
      <FormGroup label="Checkout Link">
        <TextInput
          isDisabled
          value={checkoutLink}
          button={
            <div className={styles.linkButtons}>
              <Button
                label={copied ? 'Copied!' : 'Copy'}
                onPress={copyUrl}
                variant={copied ? 'success' : 'default'}
                iconLeft="copy"
              />

              <Button
                label="Send Email"
                isPending={isEmailingCheckoutLink}
                onPress={onSendEmail}
                iconRight="arrow"
              />
            </div>
          }
        />
      </FormGroup>
    </FormRow>
  )
}
